/**
 * Signaling API Client
 * Handles WebSocket-based P2P discovery with Stytch authentication.
 * Includes auto-reconnection on network changes
 */

import { WS_URL } from "@/constants";
import { debugLog, debugError, debugWarn } from "./logger";
import type { DeviceRole, SignalMessage } from "@/types";

// Re-export types for convenience
export type { DeviceRole, SignalMessage };

// Reconnection config
const RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_ATTEMPTS = 10;

class SignalingClient {
  private ws: WebSocket | null = null;
  private messageQueue: string[] = [];
  private listeners: Map<string, Set<(msg: any) => void>> = new Map();
  private tokenProvider: (() => Promise<string | null>) | null = null;

  // Reconnection state
  private lastToken: string | null = null;
  private lastRole: DeviceRole | null = null;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private intentionalDisconnect = false;

  constructor() {
    // Listen for network changes
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.handleOnline);
      window.addEventListener("offline", this.handleOffline);
    }
  }

  private handleOnline = () => {
    console.log(
      "[Signaling] Network came back online, attempting reconnect..."
    );
    this.attemptReconnect();
  };

  private handleOffline = () => {
    console.log("[Signaling] Network went offline");
    this.notify("network-offline", {});
  };

  connect(token: string, role: DeviceRole) {
    // Store credentials for reconnection
    this.lastToken = token;
    this.lastRole = role;
    this.intentionalDisconnect = false;
    this.reconnectAttempts = 0;

    if (this.ws?.readyState === WebSocket.OPEN) return;
    if (this.ws?.readyState === WebSocket.CONNECTING) return;

    this.createConnection(token, role);
  }

  private createConnection(token: string, role: DeviceRole) {
    try {
      this.ws = new WebSocket(WS_URL);

      this.ws.onopen = () => {
        debugLog("[Signaling] Connected to WS");
        this.reconnectAttempts = 0;

        // Authenticate immediately
        const deviceId = getPersistentDeviceId();
        this.send({ type: "join", token, role, deviceId });

        // Flush queue
        while (this.messageQueue.length > 0) {
          const msg = this.messageQueue.shift();
          if (msg) this.ws?.send(msg);
        }

        // Notify listeners that connection is restored
        this.notify("connection-restored", {});
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          debugLog(`[Signaling] Received ${data.type}`, data);
          this.notify(data.type, data);
          this.notify("message", data); // Catch-all
        } catch (err) {
          debugError("[Signaling] Failed to parse message", err);
        }
      };

      this.ws.onerror = (err) => {
        debugError("[Signaling] Error:", err);
        this.notify("connection-error", { error: err });
      };

      this.ws.onclose = () => {
        debugWarn("[Signaling] Disconnected");
        this.notify("connection-closed", {});

        // Auto-reconnect if not intentional
        if (!this.intentionalDisconnect && this.lastToken && this.lastRole) {
          this.scheduleReconnect();
        }
      };
    } catch (err) {
      debugError("[Signaling] Failed to create WebSocket:", err);
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error("[Signaling] Max reconnection attempts reached");
      this.notify("connection-failed", { reason: "max-attempts" });
      return;
    }

    this.reconnectAttempts++;
    const delay = RECONNECT_DELAY_MS * Math.min(this.reconnectAttempts, 5); // Exponential backoff capped at 5x

    console.log(
      `[Signaling] Scheduling reconnect attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.attemptReconnect();
    }, delay);
  }

  private async attemptReconnect() {
    if (!this.lastRole) {
      console.warn("[Signaling] Cannot reconnect - no role stored");
      return;
    }

    let token = this.lastToken;

    // Try to get a fresh token if provider is available
    if (this.tokenProvider) {
      console.log("[Signaling] Refreshing token for reconnection...");
      try {
        const freshToken = await this.tokenProvider();
        if (freshToken) {
          token = freshToken;
          this.lastToken = freshToken;
        } else {
          console.warn("[Signaling] Token provider returned null");
        }
      } catch (err) {
        console.error("[Signaling] Failed to refresh token:", err);
      }
    }

    if (!token) {
      console.warn("[Signaling] Cannot reconnect - no token available");
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log("[Signaling] Already connected, skipping reconnect");
      return;
    }

    console.log("[Signaling] Attempting reconnect...");
    this.createConnection(token, this.lastRole);
  }

  setTokenProvider(provider: () => Promise<string | null>) {
    this.tokenProvider = provider;
  }

  // Force reconnect (called from useWebRTC)
  forceReconnect() {
    console.log("[Signaling] Force reconnect requested");
    this.reconnectAttempts = 0;

    // Close existing connection
    if (this.ws) {
      this.intentionalDisconnect = true;
      this.ws.close();
      this.ws = null;
    }

    this.intentionalDisconnect = false;
    this.attemptReconnect();
  }

  // Check if connected
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  send(data: any) {
    const payload = JSON.stringify(data);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(payload);
    } else {
      this.messageQueue.push(payload);
    }
  }

  on(type: string, callback: (msg: any) => void) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(callback);
  }

  off(type: string, callback?: (msg: any) => void) {
    if (callback) {
      this.listeners.get(type)?.delete(callback);
    } else {
      this.listeners.delete(type);
    }
  }

  private notify(type: string, data: any) {
    const callbacks = this.listeners.get(type);
    if (callbacks) {
      callbacks.forEach((callback) => callback(data));
    }
  }

  disconnect() {
    this.intentionalDisconnect = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.lastToken = null;
    this.lastRole = null;
  }
}

export const signalingClient = new SignalingClient();

function getPersistentDeviceId(): string {
  const KEY = "goodbaby_device_id";
  if (typeof window === "undefined") return "server-side-id";

  let id = localStorage.getItem(KEY);
  if (!id) {
    // Generate new ID
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      id = crypto.randomUUID();
    } else {
      // Fallback
      id =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
    }
    localStorage.setItem(KEY, id);
  }
  return id;
}
